import React, { memo } from 'react';

/**
 * ToggleButton - A toggle/switch button for on/off states
 * 
 * @example
 * <ToggleButton 
 *   isActive={isChallengeMode} 
 *   onToggle={() => setIsChallengeMode(!isChallengeMode)}
 *   activeLabel="מצב אתגר מופעל"
 *   inactiveLabel="הפעל מצב אתגר"
 *   activeIcon={<Zap />}
 *   inactiveIcon={<ZapOff />}
 * />
 */

export type ToggleVariant = 'purple' | 'blue' | 'green' | 'orange';

export interface ToggleButtonProps {
  /** Current toggle state */
  isActive: boolean;
  /** Toggle handler */
  onToggle: () => void;
  /** Label when active */
  activeLabel?: string;
  /** Label when inactive */
  inactiveLabel?: string;
  /** Icon when active */
  activeIcon?: React.ReactNode;
  /** Icon when inactive */
  inactiveIcon?: React.ReactNode;
  /** Color variant */
  variant?: ToggleVariant;
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<ToggleVariant, { active: string; inactive: string }> = {
  purple: {
    active: 'bg-purple-500 text-white border-purple-600 shadow-purple-200 dark:shadow-purple-900',
    inactive: 'bg-white/70 dark:bg-dark-surface/70 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-300 dark:hover:border-purple-700',
  },
  blue: {
    active: 'bg-blue-500 text-white border-blue-600 shadow-blue-200 dark:shadow-blue-900',
    inactive: 'bg-white/70 dark:bg-dark-surface/70 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-700',
  },
  green: {
    active: 'bg-green-500 text-white border-green-600 shadow-green-200 dark:shadow-green-900',
    inactive: 'bg-white/70 dark:bg-dark-surface/70 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-green-50 dark:hover:bg-green-900/30 hover:border-green-300 dark:hover:border-green-700',
  },
  orange: {
    active: 'bg-orange-500 text-white border-orange-600 shadow-orange-200 dark:shadow-orange-900',
    inactive: 'bg-white/70 dark:bg-dark-surface/70 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:border-orange-300 dark:hover:border-orange-700',
  },
};

export const ToggleButton: React.FC<ToggleButtonProps> = memo(({
  isActive,
  onToggle,
  activeLabel,
  inactiveLabel,
  activeIcon,
  inactiveIcon,
  variant = 'purple',
  disabled = false,
  className = '',
}) => {
  const styles = variantStyles[variant];
  const currentLabel = isActive ? activeLabel : inactiveLabel;
  const currentIcon = isActive ? activeIcon : inactiveIcon;
  
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`
        px-3 py-1.5
        rounded-full
        shadow-md
        border
        font-heebo text-sm font-bold
        flex items-center gap-2
        transition-all duration-300
        backdrop-blur-sm
        disabled:opacity-50 disabled:cursor-not-allowed
        ${isActive ? styles.active : styles.inactive}
        ${className}
      `}
    >
      {currentIcon}
      {currentLabel && <span>{currentLabel}</span>}
    </button>
  );
});

ToggleButton.displayName = 'ToggleButton';
